import { API_CONFIG } from '@/lib/api/config';
import { ApiError } from '@/lib/api/client';
import { cachedConnectionCheck, clearConnectionCheckCache } from '@/lib/api/connectionCheckCache';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

export const woocommerceApi = {
  async connect(payload: {
    businessId: string;
    storeUrl: string;
    consumerKey: string;
    consumerSecret: string;
    apiVersion?: string;
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/woocommerce/connect`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to connect WooCommerce store');
    }
    clearConnectionCheckCache(`woocommerce:${payload.businessId}`);
    return json;
  },

  async disconnect(businessId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/woocommerce/${businessId}/disconnect`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to disconnect WooCommerce store');
    }
    clearConnectionCheckCache(`woocommerce:${businessId}`);
    return json;
  },

  // Cached + in-flight-deduped for ~30s; cleared on connect/disconnect.
  async checkConnection(businessId: string) {
    return cachedConnectionCheck(`woocommerce:${businessId}`, async () => {
      const res = await fetch(`${API_CONFIG.BASE_URL}/woocommerce/${businessId}/connection`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to check WooCommerce connection');
      }
      return json;
    });
  },

  async syncProducts(businessId: string, perPage: number = 50) {
    const url = `${API_CONFIG.BASE_URL}/woocommerce/${businessId}/products/sync?perPage=${encodeURIComponent(perPage)}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to sync WooCommerce products');
    }
    return json;
  },

  async uploadImage(businessId: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_CONFIG.BASE_URL}/woocommerce/${businessId}/upload-image`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      body: formData,
    });
    const json = await res.json().catch(() => ({}));
    const url = json?.data?.url || json?.url;
    if (!res.ok || !url) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Image upload failed');
    }
    return url;
  },

  async createProduct(businessId: string, data: Record<string, any>) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/woocommerce/${businessId}/products`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create product');
    }
    return json;
  },

  async updateProduct(businessId: string, productId: string, updates: Record<string, any>) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/woocommerce/${businessId}/products/${productId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to update product');
    }
    return json;
  },

  async deleteProduct(businessId: string, productId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/woocommerce/${businessId}/products/${productId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to delete product');
    }
    return json;
  },

  async getLocalProducts(businessId: string, limit: number = 50, offset: number = 0) {
    const url = `${API_CONFIG.BASE_URL}/woocommerce/${businessId}/products/local?limit=${limit}&offset=${offset}`;
    const res = await fetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to fetch WooCommerce products');
    }
    return json;
  },

  async syncOrders(businessId: string, perPage: number = 50, status: string = 'any') {
    const url = `${API_CONFIG.BASE_URL}/woocommerce/${businessId}/orders/sync?perPage=${perPage}&status=${encodeURIComponent(status)}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to sync WooCommerce orders');
    }
    return json;
  },

  async getAbandonedOrders(businessId: string, limit: number = 50, offset: number = 0) {
    const url = `${API_CONFIG.BASE_URL}/woocommerce/${businessId}/orders/abandoned?limit=${limit}&offset=${offset}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to fetch WooCommerce abandoned orders');
    return json;
  },

  async getLocalOrders(businessId: string, limit: number = 50, offset: number = 0) {
    const url = `${API_CONFIG.BASE_URL}/woocommerce/${businessId}/orders/local?limit=${limit}&offset=${offset}`;
    const res = await fetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to fetch WooCommerce orders');
    }
    return json;
  },

  // Sync WooCommerce products to WhatsApp catalog
  async syncToCatalog(
    businessAccountId: string,
    businessId: string,
    options?: { limit?: number; skipExisting?: boolean; catalogId?: string }
  ) {
    const url = `${API_CONFIG.BASE_URL}/commerce/sync/woocommerce`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        businessAccountId,
        businessId,
        limit: options?.limit || 100,
        skipExisting: options?.skipExisting !== false,
        catalogId: options?.catalogId,
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const errorMessage = json.message || json.error || json.details || 'Failed to sync products to WhatsApp catalog';
      const error = new Error(errorMessage);
      (error as any).response = res;
      throw error;
    }
    return json;
  },
};

export function formatWooPrice(amount: number, currencyCode: string = 'USD') {
  if (Number.isNaN(Number(amount))) {
    return `${currencyCode} 0.00`;
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `${currencyCode} ${Number(amount || 0).toFixed(2)}`;
  }
}
